import { defineComponent as _defineComponent } from 'vue';
import { line, curveBasis } from "d3";
import { computed } from "vue";
import { getConnectionId } from "@/stores/workflowConnectionStore";
import { useWorkflowStateStore } from "@/stores/workflowEditorStateStore";
import { useConnectionStore } from "@/stores/workflowConnectionStore";
import { useWorkflowStepStore } from "@/stores/workflowStepStore";
export default /*#__PURE__*/ _defineComponent({
    __name: 'SVGConnection',
    props: {
        id: String,
        connection: {
            type: Object,
            required: true,
        },
        terminalPosition: {
            type: Object,
            default: null,
        },
    },
    setup(__props) {
        const props = __props;
        const ribbonMargin = 4;
        const lineShift = 30;
        const curve = line().curve(curveBasis);
        const stateStore = useWorkflowStateStore();
        const connectionStore = useConnectionStore();
        const stepStore = useWorkflowStepStore();
        const outputPos = computed(() => {
            if (props.terminalPosition) {
                return {
                    startX: props.terminalPosition.startX,
                    startY: props.terminalPosition.startY,
                };
            }
            else {
                const pos = stateStore.getOutputTerminalPosition(props.connection.output.stepId, props.connection.output.name);
                if (pos) {
                    return pos;
                }
                else {
                    return null;
                }
            }
        });
        const inputPos = computed(() => {
            if (props.terminalPosition) {
                return {
                    endX: props.terminalPosition.endX,
                    endY: props.terminalPosition.endY,
                };
            }
            else {
                const pos = stateStore.getInputTerminalPosition(props.connection.input.stepId, props.connection.input.name);
                if (pos) {
                    return pos;
                }
                else {
                    return null;
                }
            }
        });
        const position = computed(() => {
            if (inputPos.value && outputPos.value) {
                return {
                    startX: outputPos.value.startX,
                    startY: outputPos.value.startY,
                    endX: inputPos.value.endX,
                    endY: inputPos.value.endY,
                };
            }
            return null;
        });
        const outputIsMappedOver = computed(() => { var _a; return (_a = stepStore.stepMapOver[props.connection.output.stepId]) === null || _a === void 0 ? void 0 : _a.isCollection; });
        const inputIsMappedOver = computed(() => {
            var _a;
            return stepStore.stepInputMapOver[props.connection.input.stepId] &&
                ((_a = stepStore.stepInputMapOver[props.connection.input.stepId][props.connection.input.name]) === null || _a === void 0 ? void 0 : _a.isCollection);
        });
        const outputIsOptional = computed(() => {
            var _a, _b;
            return Boolean(((_a = stepStore.getStep(props.connection.output.stepId)) === null || _a === void 0 ? void 0 : _a.when) ||
                ((_b = stepStore
                    .getStep(props.connection.output.stepId)) === null || _b === void 0 ? void 0 : _b.outputs.find((output) => output.name === props.connection.output.name && output.optional)));
        });
        const connectionIsValid = computed(() => {
            return !connectionStore.invalidConnections[getConnectionId(props.connection)];
        });
        const offsets = computed(() => getLineOffsets(inputIsMappedOver.value, outputIsMappedOver.value));
        const paths = computed(() => {
            if (!position.value || !offsets.value) {
                return [];
            }
            const lines = [...Array(offsets.value.numOffsets).keys()].map((offsetIndex) => {
                const startOffset = offsets.value.startOffsets[offsetIndex] || 0;
                const endOffset = offsets.value.endOffsets[offsetIndex] || 0;
                return [
                    [position.value.startX, position.value.startY + startOffset],
                    [position.value.startX + lineShift, position.value.startY + startOffset],
                    [position.value.endX - lineShift, position.value.endY + endOffset],
                    [position.value.endX, position.value.endY + endOffset],
                ];
            });
            return lines.map((l) => curve(l));
        });
        const lineWidth = computed(() => {
            if (inputIsMappedOver.value || outputIsMappedOver.value) {
                return 2;
            }
            else {
                return 4;
            }
        });
        const connectionClass = computed(() => {
            const classList = ["connection"];
            if (outputIsOptional.value) {
                classList.push("optional");
            }
            if (!connectionIsValid.value) {
                classList.push("invalid");
            }
            return classList.join(" ");
        });
        function generateLineOffsets(inputIsMappedOver, outputIsMappedOver) {
            const _offsets = [-2 * ribbonMargin, -ribbonMargin, 0, ribbonMargin, 2 * ribbonMargin];
            let startOffsets = [0];
            let endOffsets = [0];
            let numOffsets = 1;
            if (outputIsMappedOver) {
                startOffsets = _offsets;
                numOffsets = _offsets.length;
            }
            if (inputIsMappedOver) {
                endOffsets = _offsets;
                numOffsets = _offsets.length;
            }
            return { numOffsets, startOffsets, endOffsets };
        }
        const lineOffsets = {
            "false-false": generateLineOffsets(false, false),
            "true-false": generateLineOffsets(true, false),
            "false-true": generateLineOffsets(false, true),
            "true-true": generateLineOffsets(true, true),
        };
        function getLineOffsets(inputIsMappedOver, outputIsMappedOver) {
            return lineOffsets[`${inputIsMappedOver !== null && inputIsMappedOver !== void 0 ? inputIsMappedOver : false}-${outputIsMappedOver !== null && outputIsMappedOver !== void 0 ? outputIsMappedOver : false}`];
        }
        function keyForIndex(index) {
            var _a;
            return `${(_a = props.id) !== null && _a !== void 0 ? _a : "no-key"}-${index}`;
        }
        return { __sfc: true, props, ribbonMargin, lineShift, curve, stateStore, connectionStore, stepStore, outputPos, inputPos, position, outputIsMappedOver, inputIsMappedOver, outputIsOptional, connectionIsValid, offsets, paths, lineWidth, connectionClass, generateLineOffsets, lineOffsets, getLineOffsets, keyForIndex };
    }
});
